import { cn } from "@duck/ui"
import { WorkspaceTab, WorkspaceTabOverlay } from "./WorkspaceTab"
import { AddWorkspaceTabTab } from "./AddWorkspaceTabTab"
import { workspaceTabsDefinitions } from "./workspaceTabs.definitions"
import { ChallengeService } from "@duck/api-definitions"
import { useState, useRef, forwardRef, useImperativeHandle, memo } from "react"
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
} from "@dnd-kit/core"
import {
    SortableContext,
    sortableKeyboardCoordinates,
    horizontalListSortingStrategy,
} from "@dnd-kit/sortable"
import { restrictToHorizontalAxis, restrictToParentElement } from "@dnd-kit/modifiers"
import { TranslationKey } from "@/modules/i18n"

interface Props {
    initialTabsState: {
        tabs: ChallengeService.WorkspaceTab[]
        activeTabIndex: number
    }
    defaultQuery?: string
    /** Move existing tab to split/other view; called with (tab, tabIndexInView). */
    onMoveTabToSplitView?: (tab: ChallengeService.WorkspaceTab, tabIndexInView: number) => void
    /** Add a new tab in split view (1 view) or in the other view (2 views); from + menu. */
    onAddTabInSplitView?: (tabType: ChallengeService.WorkspaceTabTypeEnum) => void
    /** Tooltip for + menu "open in split/other view": 'common.openInSplitView' or 'common.openInOtherView' */
    addTabInSplitViewTooltipKey?: TranslationKey
    /** Tooltip for the split button: 'common.moveInSplitView' (1 view) or 'common.moveToOtherView' (2 views) */
    splitViewTooltipKey?: TranslationKey
    onCloseView?: () => void
    /** Structural change: add/remove/reorder tabs, switch active tab. Parent updates state and rerenders. */
    onTabsChange?: (tabs: ChallengeService.WorkspaceTab[], activeTabIndex: number) => void
    /** Content-only change: tab data updated (e.g. SQL typing). Parent persists without rerender. */
    onTabsDataChange?: (tabs: ChallengeService.WorkspaceTab[], activeTabIndex: number) => void
}

export interface WorkspaceTabsViewRef {
    addTab: (tabType: ChallengeService.WorkspaceTabTypeEnum) => void
}

let instanceIdCounter = 0

const createInitialState = (initial: Props['initialTabsState']) => {
    const tabs = initial.tabs.map(tab => ({
        tab,
        instanceId: `tab-${instanceIdCounter++}`,
    }))
    const safeIndex = tabs.length
        ? Math.min(Math.max(initial.activeTabIndex, 0), tabs.length - 1)
        : 0
    return { tabs, activeInstanceId: tabs[safeIndex]?.instanceId ?? tabs[0]?.instanceId ?? '' }
}

/** Memoized so tab content does not re-render on drag start/end or other parent state (e.g. draggedIndex). */
const WorkspaceTabContent = memo(
    ({
        tab,
        onUpdateTabData,
        isVisible,
    }: {
        tab: ChallengeService.WorkspaceTab
        onUpdateTabData: (data: string) => void
        isVisible: boolean
    }) => {
        const Component = workspaceTabsDefinitions[tab.type].Component
        return <Component data={tab.data ?? ""} onUpdateData={onUpdateTabData} isVisible={isVisible} />
    },
)
WorkspaceTabContent.displayName = 'WorkspaceTabContent'

export const WorkspaceTabsView = forwardRef<WorkspaceTabsViewRef, Props>(({ initialTabsState, defaultQuery = '', onMoveTabToSplitView, onAddTabInSplitView, addTabInSplitViewTooltipKey, splitViewTooltipKey, onCloseView, onTabsChange, onTabsDataChange }, ref) => {
    const [{ tabs, activeInstanceId }, setTabsState] = useState(() => createInitialState(initialTabsState))
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null)

    const activeIdRef = useRef<string | null>(null)
    const tabRefsMap = useRef<Record<string, HTMLDivElement | null>>({})
    /** Latest tab data by instanceId (content-only updates, no rerender). Merged when building contract for structural changes. */
    const tabDataRef = useRef<Record<string, string>>({})

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    )

    const handleDragStart = ({ active }: DragStartEvent) => {
        activeIdRef.current = activeInstanceId
        setDraggedIndex(tabs.findIndex(t => t.instanceId === active.id))
    }

    const handleDragEnd = ({ active, over }: DragEndEvent) => {
        if (over && active.id !== over.id) {
            const oldIndex = tabs.findIndex(t => t.instanceId === active.id)
            const newIndex = tabs.findIndex(t => t.instanceId === over.id)
            const newTabs = [...tabs]
            const [removed] = newTabs.splice(oldIndex, 1)
            newTabs.splice(newIndex, 0, removed)
            setTabsState({ tabs: newTabs, activeInstanceId })
            const newActiveTabIndex = newTabs.findIndex(t => t.instanceId === activeInstanceId)
            const contractTabs = newTabs.map(t => ({ type: t.tab.type, data: tabDataRef.current[t.instanceId] ?? t.tab.data }))
            onTabsChange?.(contractTabs, newActiveTabIndex)
        }
        setDraggedIndex(null)
        queueMicrotask(() => { activeIdRef.current = null })
    }

    const handleDragCancel = () => {
        activeIdRef.current = null
        setDraggedIndex(null)
    }

    const handleAddTab = (tabType: ChallengeService.WorkspaceTabTypeEnum) => {
        const newInstanceId = `tab-${instanceIdCounter++}`
        const initialData = tabType === ChallengeService.WorkspaceTabTypeEnum.Sql ? defaultQuery : undefined
        const newTabs = [
            ...tabs,
            {
                tab: {
                    type: tabType,
                    data: initialData,
                },
                instanceId: newInstanceId,
            },
        ]
        setTabsState({
            tabs: newTabs,
            activeInstanceId: newInstanceId,
        })
        const contractTabs = newTabs.map(t => ({ type: t.tab.type, data: tabDataRef.current[t.instanceId] ?? t.tab.data }))
        onTabsChange?.(contractTabs, newTabs.length - 1)
    }

    useImperativeHandle(ref, () => ({
        addTab: handleAddTab
    }), [tabs, defaultQuery])

    const handleRemoveTab = (instanceId: string) => {
        if (tabs.length <= 1) {
            // If it's the last tab and we can close the view, do so
            onCloseView?.()
            return
        }
        const removedIndex = tabs.findIndex(t => t.instanceId === instanceId)
        const newTabs = tabs.filter(t => t.instanceId !== instanceId)
        const newActiveInstanceId =
            instanceId === activeInstanceId ? newTabs[Math.max(0, removedIndex - 1)].instanceId : activeInstanceId
        const newActiveTabIndex = newTabs.findIndex(t => t.instanceId === newActiveInstanceId)
        setTabsState({ tabs: newTabs, activeInstanceId: newActiveInstanceId })
        delete tabDataRef.current[instanceId]
        const contractTabs = newTabs.map(t => ({ type: t.tab.type, data: tabDataRef.current[t.instanceId] ?? t.tab.data }))
        onTabsChange?.(contractTabs, newActiveTabIndex)
    }

    const draggedTab = draggedIndex !== null ? tabs[draggedIndex] : null

    const handleUpdateTabData = (instanceId: string, data: string) => {
        const index = tabs.findIndex(t => t.instanceId === instanceId)
        if (index === -1) return
        tabDataRef.current[instanceId] = data
        const newActiveIndex = tabs.findIndex(t => t.instanceId === activeInstanceId)
        const contractTabs = tabs.map(t => ({ type: t.tab.type, data: t.instanceId === instanceId ? data : (tabDataRef.current[t.instanceId] ?? t.tab.data) }))
        // Content-only: persist via parent ref, no local setState. SqlTab manages its own state; no rerender needed.
        ;(onTabsDataChange ?? onTabsChange)?.(contractTabs, newActiveIndex)
    }

    return (
        <div className="h-full min-h-0 w-full flex flex-col">
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                <div className="pt-5 min-w-0 overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                    <div
                        role="tablist"
                        aria-orientation="horizontal"
                        className={cn('flex items-end -space-x-2 w-fit ms-2')}
                    >
                        <SortableContext
                            items={tabs.map(t => t.instanceId)}
                            strategy={horizontalListSortingStrategy}
                        >
                            {tabs.map((tab, index) => (
                                <WorkspaceTab
                                    key={tab.instanceId}
                                    sortableId={tab.instanceId}
                                    tabType={tab.tab.type}
                                    index={index}
                                    label={workspaceTabsDefinitions[tab.tab.type].label}
                                    isActive={tab.instanceId === (activeIdRef.current ?? activeInstanceId)}
                                    isDragActive={draggedIndex !== null}
                                    tabRef={(el) => {
                                        tabRefsMap.current[tab.instanceId] = el
                                    }}
                                    onActivateTab={() => {
                                        const newActiveIndex = tabs.findIndex(t => t.instanceId === tab.instanceId)
                                        setTabsState({ tabs, activeInstanceId: tab.instanceId })
                                        const contractTabs = tabs.map(t => ({ type: t.tab.type, data: tabDataRef.current[t.instanceId] ?? t.tab.data }))
                                        onTabsChange?.(contractTabs, newActiveIndex)
                                        tabRefsMap.current[tab.instanceId]?.scrollIntoView({
                                            behavior: 'smooth',
                                            block: 'nearest',
                                            inline: 'nearest',
                                        })
                                    }}
                                    onRemoveTab={tabs.length > 1 || onCloseView ? () => handleRemoveTab(tab.instanceId) : undefined}
                                    onOpenInSplitView={onMoveTabToSplitView ? () => onMoveTabToSplitView(tab.tab, index) : undefined}
                                    splitViewTooltipKey={splitViewTooltipKey}
                                />
                            ))}
                        </SortableContext>
                        <AddWorkspaceTabTab onAddTab={handleAddTab} onOpenInSplitView={onAddTabInSplitView} openInSplitViewTooltipKey={addTabInSplitViewTooltipKey} />
                    </div>
                </div>
                <DragOverlay dropAnimation={null}>
                    {draggedTab && (
                        <WorkspaceTabOverlay
                            label={workspaceTabsDefinitions[draggedTab.tab.type].label}
                            isActive={draggedTab.instanceId === activeInstanceId}
                        />
                    )}
                </DragOverlay>
            </DndContext>
            <div className="border-t border-border flex-1 min-w-0 min-h-0 flex flex-col">
                <div className="relative flex-1 min-h-0 min-w-0">
                    {tabs.map(({ instanceId, tab }) => (
                        <div
                            key={instanceId}
                            className={cn(
                                "absolute inset-0 overflow-auto custom-scrollbar",
                                instanceId !== activeInstanceId && "invisible pointer-events-none",
                                instanceId === activeInstanceId && "z-[1]"
                            )}
                            aria-hidden={instanceId !== activeInstanceId}
                        >
                            <WorkspaceTabContent
                                tab={tab}
                                onUpdateTabData={(data) => handleUpdateTabData(instanceId, data)}
                                isVisible={instanceId === activeInstanceId}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
})

WorkspaceTabsView.displayName = 'WorkspaceTabsView'